<template>
  <div class="p-3 p-md-4">

    <!-- Header -->
    <div class="d-flex align-items-center mb-4">
      <div class="bg-light rounded-circle d-flex align-items-center justify-content-center border me-3"
        style="width: 56px; height: 56px;">
        <i class="fa-solid fa-user text-secondary fs-4"></i>
      </div>
      <div class="lh-1">
        <h5 class="mb-1">{{ form.fullname || $t('Edit Profile') }}</h5>
        <span class="badge bg-light text-secondary border">
          <i class="fa-solid fa-id-badge me-1"></i>{{ form.role || '-' }}
        </span>
        <span class="text-muted ms-2 small">
          <i class="fa-solid fa-at me-1"></i>{{ form.username }}
        </span>
      </div>
    </div>

    <div v-if="loading" class="text-center text-muted py-5">
      <i class="fa-solid fa-spinner fa-spin me-2"></i>{{ $t('Loading...') }}
    </div>

    <div v-else class="row g-4">

      <!-- Profile information -->
      <div class="col-lg-7">
        <div class="border rounded-3 p-3 p-md-4 h-100">
          <h6 class="fw-bold mb-3">
            <i class="fa-solid fa-user-pen me-2 text-primary"></i>{{ $t('Profile Information') }}
          </h6>

          <div class="mb-3">
            <label for="fullname" class="form-label small fw-bold text-secondary">
              {{ $t('Full Name') }}
            </label>
            <div class="custom-input-group input-group input-group-sm">
              <span class="input-group-text bg-white border-end-0 text-muted">
                <i class="fa-regular fa-id-card"></i>
              </span>
              <input
                v-model="v$.form.fullname.$model"
                type="text"
                class="form-control border-start-0 ps-0"
                id="fullname"
              >
            </div>
            <div v-for="error of v$.form.fullname.$errors" class="d-block invalid-feedback" :key="error.$uid">
              {{ error.$message }}
            </div>
          </div>

          <div class="mb-3">
            <label for="email" class="form-label small fw-bold text-secondary">
              {{ $t('Email Address') }}
            </label>
            <div class="custom-input-group input-group input-group-sm">
              <span class="input-group-text bg-white border-end-0 text-muted">
                <i class="fa-regular fa-envelope"></i>
              </span>
              <input
                v-model="v$.form.email.$model"
                type="email"
                class="form-control border-start-0 ps-0"
                id="email"
                placeholder="name@example.com"
              >
            </div>
            <div v-for="error of v$.form.email.$errors" class="d-block invalid-feedback" :key="error.$uid">
              {{ error.$message }}
            </div>
          </div>

          <div class="row">
            <div class="col-sm-6 mb-3">
              <label for="telephone1" class="form-label small fw-bold text-secondary">
                {{ $t('Telephone') }}
              </label>
              <div class="custom-input-group input-group input-group-sm">
                <span class="input-group-text bg-white border-end-0 text-muted">
                  <i class="fa-solid fa-phone"></i>
                </span>
                <input
                  v-model="v$.form.telephone1.$model"
                  type="tel"
                  class="form-control border-start-0 ps-0"
                  id="telephone1"
                  placeholder="08x-xxx-xxxx"
                >
              </div>
              <div v-for="error of v$.form.telephone1.$errors" class="d-block invalid-feedback" :key="error.$uid">
                {{ error.$message }}
              </div>
            </div>

            <div class="col-sm-6 mb-3">
              <label for="telephone2" class="form-label small fw-bold text-secondary">
                {{ $t('Telephone') }} 2 <span class="text-muted fw-normal">({{ $t('Optional') }})</span>
              </label>
              <div class="custom-input-group input-group input-group-sm">
                <span class="input-group-text bg-white border-end-0 text-muted">
                  <i class="fa-solid fa-phone"></i>
                </span>
                <input
                  v-model="v$.form.telephone2.$model"
                  type="tel"
                  class="form-control border-start-0 ps-0"
                  id="telephone2"
                  placeholder="-"
                >
              </div>
              <div v-for="error of v$.form.telephone2.$errors" class="d-block invalid-feedback" :key="error.$uid">
                {{ error.$message }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Change password -->
      <div class="col-lg-5">
        <div class="border rounded-3 p-3 p-md-4 h-100">
          <h6 class="fw-bold mb-1">
            <i class="fa-solid fa-lock me-2 text-primary"></i>{{ $t('Change Password') }}
          </h6>
          <p class="text-muted small mb-3">
            {{ $t('Leave blank if you do not want to change it.') }}
          </p>

          <div class="mb-3">
            <label for="current_password" class="form-label small fw-bold text-secondary">
              {{ $t('Current Password') }}
            </label>
            <div class="custom-input-group input-group input-group-sm">
              <span class="input-group-text bg-white border-end-0 text-muted">
                <i class="fa-solid fa-key"></i>
              </span>
              <input
                v-model.trim="v$.form.current_password.$model"
                :type="showCurrent ? 'text' : 'password'"
                class="form-control border-start-0 border-end-0 ps-0"
                id="current_password"
                placeholder="••••••••"
                autocomplete="current-password"
              >
              <span class="input-group-text bg-white border-start-0 text-muted cursor-pointer" @click="showCurrent = !showCurrent">
                <i class="far" :class="showCurrent ? 'fa-eye' : 'fa-eye-slash'"></i>
              </span>
            </div>
            <div v-for="error of v$.form.current_password.$errors" class="d-block invalid-feedback" :key="error.$uid">
              {{ error.$message }}
            </div>
          </div>

          <div class="mb-3">
            <label for="new_password" class="form-label small fw-bold text-secondary">
              {{ $t('New Password') }}
            </label>
            <div class="custom-input-group input-group input-group-sm">
              <span class="input-group-text bg-white border-end-0 text-muted">
                <i class="fa-solid fa-lock"></i>
              </span>
              <input
                v-model.trim="v$.form.new_password.$model"
                :type="showNew ? 'text' : 'password'"
                class="form-control border-start-0 border-end-0 ps-0"
                id="new_password"
                placeholder="••••••••"
                autocomplete="new-password"
              >
              <span class="input-group-text bg-white border-start-0 text-muted cursor-pointer" @click="showNew = !showNew">
                <i class="far" :class="showNew ? 'fa-eye' : 'fa-eye-slash'"></i>
              </span>
            </div>
            <div v-for="error of v$.form.new_password.$errors" class="d-block invalid-feedback" :key="error.$uid">
              {{ error.$message }}
            </div>
          </div>

          <div class="mb-3">
            <label for="new_password_confirmation" class="form-label small fw-bold text-secondary">
              {{ $t('Confirm Password') }}
            </label>
            <div class="custom-input-group input-group input-group-sm">
              <span class="input-group-text bg-white border-end-0 text-muted">
                <i class="fa-solid fa-check-double"></i>
              </span>
              <input
                v-model.trim="v$.form.new_password_confirmation.$model"
                type="password"
                class="form-control border-start-0 ps-0"
                id="new_password_confirmation"
                placeholder="••••••••"
                autocomplete="new-password"
              >
            </div>
            <div v-for="error of v$.form.new_password_confirmation.$errors" class="d-block invalid-feedback" :key="error.$uid">
              {{ error.$message }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Actions -->
    <div v-if="!loading" class="d-flex justify-content-end gap-2 mt-4">
      <button type="button" class="btn btn-light border" @click="resetForm">
        {{ $t('Reset') }}
      </button>
      <ladda
        text="Save Changes"
        :btn-class="'btn btn-theme text-white fw-bold px-4'"
        :loading="btnLoading"
        @click="handleSave"
        :disabled="v$.$invalid"
      />
    </div>
  </div>
</template>

<script>
import { useVuelidate } from '@vuelidate/core';
import { required, email, numeric, minLength, maxLength, sameAs, requiredIf, helpers } from '@vuelidate/validators';
import _ from 'lodash';
import Ladda from '@/plugins/Ladda.vue';
import { Auth } from '@/services/endpoint/auth-api.service';
import { useAuthStore } from '@/stores/auth.store';

export default {
  name: 'Profile',
  components: { Ladda },
  setup: () => ({ v$: useVuelidate() }),
  data() {
    return {
      loading: true,
      btnLoading: false,
      showCurrent: false,
      showNew: false,
      original: {},
      form: {
        fullname: '',
        username: '',
        email: '',
        telephone1: '',
        telephone2: '',
        role: '',
        current_password: '',
        new_password: '',
        new_password_confirmation: ''
      }
    };
  },
  validations() {
    return {
      form: {
        fullname: {
          required: helpers.withMessage(this.$t('Please enter fullname'), required)
        },
        email: {
          required: helpers.withMessage(this.$t('Please enter email'), required),
          email: helpers.withMessage(this.$t('Email invalid'), email)
        },
        telephone1: {
          required: helpers.withMessage(this.$t('Please enter telephone'), required),
          numeric: helpers.withMessage(this.$t('Telephone must be only numeric.'), numeric),
          minLength: helpers.withMessage(
            ({ $params }) => this.$t('Telephone must be at least') + ' ' + $params.min + ' ' + this.$t('characters long.'),
            minLength(9)
          ),
          maxLength: helpers.withMessage(
            ({ $params }) => this.$t('Telephone cannot exceed') + ' ' + $params.max + ' ' + this.$t('characters.'),
            maxLength(10)
          )
        },
        telephone2: {
          numeric: helpers.withMessage(this.$t('Telephone must be only numeric.'), numeric),
          minLength: helpers.withMessage(
            ({ $params }) => this.$t('Telephone must be at least') + ' ' + $params.min + ' ' + this.$t('characters long.'),
            minLength(9)
          ),
          maxLength: helpers.withMessage(
            ({ $params }) => this.$t('Telephone cannot exceed') + ' ' + $params.max + ' ' + this.$t('characters.'),
            maxLength(10)
          )
        },
        current_password: {
          requiredIf: helpers.withMessage(this.$t('Please enter current password'), requiredIf(() => !!this.form.new_password))
        },
        new_password: {
          minLength: helpers.withMessage(
            ({ $params }) => this.$t('password must be at least') + ' ' + $params.min + ' ' + this.$t('characters long.'),
            minLength(8)
          ),
          maxLength: helpers.withMessage(
            ({ $params }) => this.$t('password cannot exceed') + ' ' + $params.max + ' ' + this.$t('characters.'),
            maxLength(16)
          )
        },
        new_password_confirmation: {
          sameAsPassword: helpers.withMessage(this.$t('Passwords do not match.'), sameAs(this.form.new_password))
        }
      }
    };
  },
  async mounted() {
    await this.fetchProfile();
  },
  methods: {
    async fetchProfile() {
      this.loading = true;
      try {
        const res = await Auth.getProfile();
        const data = res.data.data;
        this.form.fullname = data.fullname ?? '';
        this.form.username = data.username ?? '';
        this.form.email = data.email ?? '';
        this.form.telephone1 = data.telephone1 ?? '';
        this.form.telephone2 = data.telephone2 ?? '';
        this.form.role = data.role ?? '';
        this.original = _.cloneDeep(this.form);
      } catch (err) {
        this.$toast.warn(this.$t(err?.response?.data?.message) || this.$t('Something went wrong'));
      } finally {
        this.loading = false;
      }
    },
    resetForm() {
      this.form = _.cloneDeep({
        ...this.original,
        current_password: '',
        new_password: '',
        new_password_confirmation: ''
      });
      this.v$.$reset();
    },
    async handleSave() {
      this.v$.$touch();
      if (this.v$.$invalid) return;

      this.btnLoading = true;
      const params = {
        fullname: this.form.fullname,
        email: this.form.email,
        telephone1: this.form.telephone1,
        telephone2: this.form.telephone2 || ''
      };
      // ส่ง field รหัสผ่านเฉพาะตอนตั้งใจเปลี่ยน
      if (this.form.new_password) {
        params.current_password = this.form.current_password;
        params.new_password = this.form.new_password;
        params.new_password_confirmation = this.form.new_password_confirmation;
      }

      try {
        const res = await Auth.updateProfile(params);
        const data = res.data.data;

        // sync ชื่อ/role/เบอร์ ที่โชว์บน TopMenu (auth store)
        const store = useAuthStore();
        if (store.user) {
          store.user = {
            ...store.user,
            fullname: data.fullname,
            telephone1: data.telephone1,
            telephone2: data.telephone2,
            role: data.role
          };
        }

        this.original = _.cloneDeep({ ...this.form, current_password: '', new_password: '', new_password_confirmation: '' });
        this.form.current_password = '';
        this.form.new_password = '';
        this.form.new_password_confirmation = '';
        this.v$.$reset();

        this.$toast.success('<B>' + this.$t('Success') + '</B>\n' + this.$t('Update Profile Success'));
      } catch (err) {
        this.$toast.warn(this.$t(err?.response?.data?.message) || this.$t('Something went wrong'));
      } finally {
        this.btnLoading = false;
      }
    }
  }
};
</script>
